import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    FormControl,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    ListItemText,
    Tooltip,
    CircularProgress
} from '@mui/material';
import styles from '../../../Samarth Module/components/dropdown/MultiSelectWithInput.module.css';
import { EraserIcon } from 'lucide-react';
import SnackBarMessage from 'Samarth Module/components/snackbar/snackbar';
import PropTypes from "prop-types";

function isSelectAllAction(value) {
    return Array.isArray(value) && value.includes("Select all");
}

function getValueCounts(value) {
    return value.reduce((counts, item) => {
        const key = String(item);
        counts[key] = (counts[key] || 0) + 1;
        return counts;
    }, {});
}
function normalizeOrLossTreeYearValue(value) {
    const valueCounts = getValueCounts(value);
    const seen = new Set();

    return value.reduce((normalizedValue, item) => {
        const key = String(item);

        if (seen.has(key)) return normalizedValue;

        seen.add(key);

        if (valueCounts[key] === 1) {
            normalizedValue.push(key);
        }

        return normalizedValue;
    }, []);
}

const MultiSelectWithSearchInput = ({ label, options = [], filterValues, handleFilter, disabled, isLabelShow = true, type, isMultiSelect = true, defaultValue = null, showSelectAll = true }) => {
    const alertTypelist = ['Daily Phasing', 'MM Screen Input', 'RMPM Alert', 'Agile OR', 'Norm Alert',
        'Contract Alert', 'Contract PR2 Alert', 'Weekly OR', 'Supplier Insights', 'Manpower Shortage',
        'Or Loss Tree', 'Prioritization Matrix', 'Dispatch Priority', 'Hands Free VMI', 'PO Priority'];
    const [selectedValues, setSelectedValues] = useState(() => {
        // Initialize with filterValues if available, otherwise use defaultValue
        const initialValues = filterValues?.[label] ?? filterValues;

        if (Array.isArray(initialValues) && initialValues.length > 0) {
            return initialValues;
        }

        if (typeof initialValues === "string" && initialValues) {
            return [initialValues];
        }

        if (defaultValue !== null && defaultValue !== undefined) {
            return Array.isArray(defaultValue)
                ? defaultValue
                : [defaultValue];
        }

        return [];
    });
    const [searchText, setSearchText] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options?.slice(0, options.length));
    const [labelTruncation, setLabelTruncation] = useState(false);
    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: "",
        type: "error",
    });
    const labelRef = useRef(null);
    const compRef = useRef(null);
    const ITEMS_PER_BATCH = 25;
    const [visibleCount, setVisibleCount] = useState(ITEMS_PER_BATCH);

    const excludeFilterOptions = useMemo(() => {
        if (label === "Root cause" && type === "Contract Alert") {
            return options.filter(opt => opt !== "Contract Available");
        }

        if (label === "Root cause" && type === "Contract PR2 Alert") {
            return options.filter(opt => opt !== "Contract Available");
        }

        if (label === "Alert status" && ['RMPM Alert', 'Agile OR'].includes(type)) {
            return ['Open', 'Accepted', 'Rejected'];
        }
        if (label === "Alert status" && ['Contract Alert'].includes(type)) {
            return ['Open', 'Accepted', 'Rejected', 'Closed'];
        }

        return options;
    }, [options, label, type]);


    useEffect(() => {
        const initialValues = filterValues?.[label] || filterValues || [];

        setSelectedValues(() => {
            if (typeof initialValues === 'string') {
                return [initialValues];
            }

            if (label === 'PO Ageing' && Array.isArray(initialValues)) {
                return [initialValues[initialValues.length - 1]];
            }

            if (Array.isArray(initialValues)) {
                const filtered = initialValues.filter(
                    val =>
                        !(
                            (label === 'Root cause' ||
                                label === 'Plan' ||
                                label === 'Week' ||
                                label === 'UOM') &&
                            type === 'Contract Alert' &&
                            val === 'Contract Available'
                        )
                );

                // If no values remain after filtering and a defaultValue exists, use it.
                if (filtered.length === 0 && defaultValue !== null) {
                    return Array.isArray(defaultValue)
                        ? defaultValue
                        : [defaultValue];
                }

                return filtered;
            }

            // If initialValues is empty or undefined and a defaultValue exists.
            if (defaultValue !== null) {
                return Array.isArray(defaultValue)
                    ? defaultValue
                    : [defaultValue];
            }

            return [];
        });
    }, [filterValues, label, excludeFilterOptions, type, defaultValue]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            const filtered = excludeFilterOptions?.filter((opt) =>
                (typeof opt === 'string' && opt?.toLowerCase().includes(searchText?.toLowerCase())) ||
                (typeof opt === 'number' && opt?.toString().includes(searchText))
            );
            setFilteredOptions(searchText ? filtered?.slice(0, filtered.length) : excludeFilterOptions?.slice(0, excludeFilterOptions.length));
        }, 300);
        return () => clearTimeout(timeout);
    }, [searchText, excludeFilterOptions]);

    // if label is truncated then only tooltip will show
    useEffect(() => {
        setLabelTruncation(labelRef.current.scrollWidth > labelRef.current.clientWidth)
    }, [label]);

    const closeSnackbar = useCallback(() => {
        setSnackBarObject((prev) => ({ ...prev, snackbarOpen: false }));
    }, []);

    const REQUIRED_OPTION_MESSAGE = (labelName) =>
        `At least one option in ${labelName} is required.`;

    function showRequiredOptionError() {
        setSnackBarObject({
            snackbarOpen: true,
            message: REQUIRED_OPTION_MESSAGE(label),
            type: "error",
        });
    }

    function applySelectedValues(values) {
        setSelectedValues(values);
        handleFilter(label, values);
    }

    function normalizePoAgeingValue(value) {
        if (label !== "PO Ageing") return value;
        return [value[value.length - 1]];
    }

    function hasAllFilteredOptionsSelected() {
        return filteredOptions.every((opt) =>
            selectedValues.some((val) => String(val) === String(opt)),
        );
    }

    function removeFilteredOptionsFromSelection() {
        return selectedValues.filter((val) =>
            !filteredOptions.some((opt) => String(opt) === String(val)),
        );
    }

    function mergeFilteredOptionsWithSelection() {
        return [...new Set([...selectedValues, ...filteredOptions])];
    }

    function handleSelectAllAction() {
        const newSelectedValues = hasAllFilteredOptionsSelected()
            ? removeFilteredOptionsFromSelection()
            : mergeFilteredOptionsWithSelection();

        applySelectedValues(newSelectedValues);
    }

    function isOrLossTreeYearFilter(value) {
        return type === "Or Loss Tree" && label === "Year" && Array.isArray(value);
    }

    function handleOrLossTreeYearSelection(value) {
        const normalizedValue = normalizeOrLossTreeYearValue(value);

        if (normalizedValue.length === 0) {
            showRequiredOptionError();
            return;
        }

        applySelectedValues(normalizedValue);
    }

    function isRequiredDailyPhasingFilter() {
        return (
            type === "Daily Phasing" &&
            ["Week", "Plan", "UOM"].includes(label)
        );
    }

    function handleRequiredSelection(value) {
        if (value.length === 0) {
            showRequiredOptionError();
            return;
        }

        applySelectedValues(value);
    }

    const handleFilterClick = (event) => {
        const value = normalizePoAgeingValue(event.target.value);

        if (isSelectAllAction(value)) {
            handleSelectAllAction();
            return;
        }

        if (isOrLossTreeYearFilter(value)) {
            handleOrLossTreeYearSelection(value);
            return;
        }

        if (isRequiredDailyPhasingFilter()) {
            handleRequiredSelection(value);
            return;
        }

        applySelectedValues(value);
    };

    const handleSearchChange = (event) => {
        const newSearchTerm = event.target.value.toLowerCase();
        setSearchText(newSearchTerm);
        setSearchText(event.target.value);
    };

    const handleMenuScroll = (event) => {
        const list = event.target;

        if (list.scrollTop + list.clientHeight >= list.scrollHeight - 5) {
            setVisibleCount((prev) =>
                Math.min(prev + ITEMS_PER_BATCH, filteredOptions.length)
            );
        }
    };
    // Handle outside clicks
    const handleClickOutside = (event) => {
        if (compRef.current && !compRef.current.contains(event.target)) {
            setSearchText('');
        }
    };

    // Add event listener on mount
    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            // Cleanup on unmount
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const clearValue = useCallback(() => {
        const shouldResetToDefault =
            defaultValue !== null &&
            (
                type === 'Daily Phasing' ||
                type === 'MM Screen Input' ||
                (
                    type === 'Or Loss Tree' &&
                    (label === 'Data UOM' || label === 'Year')
                )
            );

        let resetValue = [];

        if (shouldResetToDefault) {
            resetValue = Array.isArray(defaultValue)
                ? defaultValue
                : [defaultValue];
        }

        setSelectedValues(resetValue);
        handleFilter(label, resetValue);
        setSearchText('');
    }, [label, handleFilter, type, defaultValue]);

    const isAllSelected = filteredOptions.every(opt => {
        if (Array.isArray(selectedValues)) {
            return selectedValues.some(val => String(val) === String(opt));
        }
        return String(selectedValues) === String(opt);
    }) && filteredOptions.length > 0;

    const isChecked = (opt) => {
        if (Array.isArray(selectedValues)) {
            return selectedValues.some(val => String(val) === String(opt));
        }
        return String(selectedValues) === String(opt);
    };

    const compareValues = (a, b) => {
        // Numeric sort if both values are numbers
        if (!Number.isNaN(a) && !Number.isNaN(b)) {
            return Number(a) - Number(b);
        }

        // String sort
        return String(a).localeCompare(String(b));
    };

    function isDefaultComparisonApplicable() {
        return (
            (type === 'Daily Phasing' ||
                type === 'MM Screen Input' ||
                type === 'Or Loss Tree') &&
            defaultValue !== null
        );
    }

    function isDefaultMMWeekSelection() {
        if (
            type !== 'MM Screen Input' ||
            label !== 'Week' ||
            !defaultValue
        ) {
            return false;
        }

        return (
            (typeof selectedValues === 'string' &&
                selectedValues === defaultValue) ||
            (
                Array.isArray(selectedValues) &&
                selectedValues.length === 1 &&
                selectedValues[0] === defaultValue
            )
        );
    }

    function isDefaultOrLossTreeDataUomSelection() {
        return (
            type === 'Or Loss Tree' &&
            label === 'Data UOM' &&
            defaultValue &&
            JSON.stringify(selectedValues) === JSON.stringify(defaultValue)
        );
    }

    function areArrayValuesDifferent() {
        return (
            JSON.stringify([...defaultValue].sort(compareValues)) !==
            JSON.stringify([...selectedValues].sort(compareValues))
        );
    }

    // Check if current value is different from default value
    const isDifferentFromDefault = useCallback(() => {
        if (!isDefaultComparisonApplicable()) {
            return true;
        }

        if (isDefaultMMWeekSelection()) {
            return false;
        }

        if (isDefaultOrLossTreeDataUomSelection()) {
            return false;
        }

        if (
            Array.isArray(defaultValue) &&
            Array.isArray(selectedValues)
        ) {
            return areArrayValuesDifferent();
        }
        return defaultValue !== selectedValues;
    }, [type, defaultValue, selectedValues, label]);

    const isVisibleSelectAll = (type, label) => {
        if (type === 'Daily Phasing' || type === 'MM Screen Input' || type === 'Or Loss Tree') {
            if (label === "Week" || label === 'Plan' || label === 'UOM' || label === 'Data UOM') {
                if (type === 'Or Loss Tree' && label === "Week") {
                    return true;
                }
                else {
                    return false;
                }
            } else {
                return true;
            }
        } else {
            return true;
        }
    };

    return (
        <>
            <div className={`${styles.labelFilterGroup} ${type === 'edit user' ? styles.editUser : ''}`} ref={compRef}>
                <Tooltip title={labelTruncation ? label : ''}
                    componentsProps={{
                        tooltip: {
                            sx: {
                                color: '#4D4D4F',
                            }
                        }
                    }}
                    placement='top'
                >
                    <label ref={labelRef} className={styles.label} style={{ color: disabled ? "rgb(77 77 79 / 42%)" : '#4D4D4F' }}>
                        {isLabelShow ? label : ''}{alertTypelist.includes(type) && selectedValues.length > 0 && isDifferentFromDefault() && label !== 'PO Ageing' && <EraserIcon className={styles.eraserIcon} onClick={clearValue} />}
                    </label>
                </Tooltip>
                <FormControl fullWidth>
                    <Select
                        id={`${label}-select`}
                        className={`${styles.filterFields} ${type === 'edit user' ? styles.filterFieldsUsers : ''}`}
                        multiple={isMultiSelect}
                        displayEmpty
                        value={selectedValues}
                        onChange={handleFilterClick}
                        disabled={disabled}
                        input={
                            <OutlinedInput />
                        }
                        style={{ backgroundColor: '#fff' }}
                        renderValue={() => {
                            if (typeof selectedValues === 'string') {
                                return selectedValues;
                            }

                            if (selectedValues.length === 0) {
                                return isLabelShow ? 'All' : label;
                            }

                            if (
                                selectedValues.length === excludeFilterOptions.length &&
                                excludeFilterOptions.length > 1
                            ) {
                                return 'All';
                            }

                            return selectedValues.join(', ');
                        }}
                        MenuProps={{
                            autoFocus: false,
                            PaperProps: {
                                style: {
                                    maxHeight: '200px',
                                },
                                sx: {
                                    '&::-webkit-scrollbar': {
                                        width: '7px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                        borderRadius: '7px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        borderRadius: '7px',
                                    }
                                },
                                onScroll: handleMenuScroll,
                            },
                        }}
                    >
                        {label !== 'PO Ageing' && (
                            <MenuItem key="search" sx={{ padding: 0, borderBottom: '1px solid #ccc', '&.MuiMenuItem-root:hover': { backgroundColor: 'transparent' } }}>
                                <input type="text"
                                    placeholder="Search..."
                                    value={searchText}
                                    onChange={handleSearchChange}
                                    className={styles.SearchInput}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    style={{ border: 'none', height: '30px', width: '100%', paddingLeft: '5px' }}
                                />
                            </MenuItem>)}
                        {showSelectAll && isVisibleSelectAll(type, label) && label !== 'PO Ageing' &&
                            <MenuItem value="Select all" sx={{
                                padding: '5px',
                                '& .MuiTypography-root': {
                                    fontSize: '12px',
                                },
                            }}>
                                <Checkbox checked={isAllSelected} sx={{ padding: '2px' }} />
                                <ListItemText primary="Select all" />
                            </MenuItem>
                        }
                        {filteredOptions.slice(0, visibleCount).map((opt, ind) => {
                            return (
                                <MenuItem key={`${opt}_${ind}`} value={opt} sx={{
                                    padding: '5px',
                                    '& .MuiTypography-root': {
                                        fontSize: '12px',
                                    },
                                }}>
                                    {isMultiSelect && !(label === 'Plan' || label === 'UOM' || (type === 'MM Screen Input' && label === 'Week') || (type === 'Or Loss Tree' && label === 'Data UOM')) &&
                                        <Checkbox checked={isChecked(opt)} sx={{ padding: '2px' }} />
                                    }
                                    <ListItemText primary={opt} />
                                </MenuItem>
                            );
                        })}
                        {visibleCount < filteredOptions.length && (
                            <MenuItem disabled sx={{ justifyContent: 'center' }}>
                                <CircularProgress size={18} />
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </div>
            {snackBarObject["snackbarOpen"] && (
                <SnackBarMessage
                    type={snackBarObject["type"]}
                    snackbarMessage={snackBarObject["message"]}
                    snackbarOpen={snackBarObject["snackbarOpen"]}
                    setSnackbarOpen={closeSnackbar}
                />
            )
            }
        </>
    );
};

MultiSelectWithSearchInput.propTypes = {
    label: PropTypes.string.isRequired,
    options: PropTypes.array,
    filterValues: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.string,
        PropTypes.object,
    ]),
    handleFilter: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    isLabelShow: PropTypes.bool,
    type: PropTypes.string,
    isMultiSelect: PropTypes.bool,
    defaultValue: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.string,
        PropTypes.number,
    ]),
    showSelectAll: PropTypes.bool,
};

export default MultiSelectWithSearchInput;